import { Button, Dialog, Step, Steps } from "@omnabase/ui";
import { agentMark } from "../../lib/connections";
import { AI_AGENTS, agentTakesKey, setupStepsFor, type AgentId, type Setup, type SetupStep } from "@omnabase/shared";

// M9 follow-up "Your own AI" for every agent (DECISIONS "2026-10-05 — Your own AI for every agent"): the chosen agent's steps in shared's
// words (`setupStepsFor`, verified per vendor in docs/research/mcp-clients-2026-10-05.md). The send-and-sections round (2026-10-23; brief
// part 2): every app has its own dialog, opened from the card's menu (`?connect=<app id>`), its steps in the kit's open stepper (`Steps`,
// `Step`: every step shown at once, numbered, Copy where there is a line), ending with what to say; no intro, no chooser, no block of explanations.

const labelOf = (id: AgentId) => AI_AGENTS.find((a) => a.id === id)?.label ?? id;
/** the apps the card's menu names, in the founder's order (Claude has its own button) */
export const MENU_AGENTS: readonly AgentId[] = AI_AGENTS.map((a) => a.id).filter((id) => id !== "claude");
/** a dialog's title: "Connect <app>", and for the app called Other "Connect another app" */
export const connectTitle = (id: AgentId) => (id === "other" ? "Connect another app" : `Connect ${labelOf(id)}`);

/** One app's steps in a dialog, with this workspace's address filled in. */
export function ConnectDialog({ agent, address, onCopy, onClose }: { agent: AgentId; address: string; onCopy: (text: string, what: string) => void; onClose: () => void }) {
  return (
    <Dialog title={connectTitle(agent)} icon={agentMark(agent)} width={640} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      {address && <AgentSteps setup={setupStepsFor(agent, { address })} onCopy={onCopy} />}
    </Dialog>
  );
}

/** One app's steps as the kit's open stepper: each step's title, its text, and the line to copy with its Copy button. */
export function AgentSteps({ setup, onCopy }: { setup: Setup; onCopy: (text: string, what: string) => void }) {
  return (
    <Steps form="open" label={`Set up ${setup.title}`}>
      {setup.steps.map((step, i) => <Step key={i} n={i + 1} title={step.title}><StepLine step={step} onCopy={onCopy} /></Step>)}
    </Steps>
  );
}
function StepLine({ step, onCopy }: { step: SetupStep; onCopy: (text: string, what: string) => void }) {
  return (
    <>
      <p className="ob-step__text">{step.text}</p>
      {step.command && <div className="ob-secret ob-step__cmd"><code>{step.command}</code><Button size="sm" icon="copy" aria-label={`Copy: ${step.title}`} onClick={() => onCopy(step.command!, "Line")}>Copy</Button></div>}
    </>
  );
}

/** The key dialog's lines in the same stepper: the chosen agent's own with the key in them; an agent that only signs in takes no key, so the
 *  dialog says so and gives the header any other app sends. */
export function KeyAgentSteps({ agent, address, secret, onCopy }: { agent: AgentId; address: string; secret: string; onCopy: (text: string, what: string) => void }) {
  if (agentTakesKey(agent)) return <AgentSteps setup={setupStepsFor(agent, { address, key: secret })} onCopy={onCopy} />;
  return (
    <>
      <p className="ob-step__text">{labelOf(agent)} signs in and takes no key. For an app that takes a header instead:</p>
      <AgentSteps setup={setupStepsFor("other", { address, key: secret })} onCopy={onCopy} />
    </>
  );
}
